import { useScopeReports } from "@/api/report-query";
import { useScopeStore } from "@/stores/scope";
import { AuditTarget } from "@soft-db/monitoring-api-client";
import { For, Show } from "@soft-db/soft-utils";
import { Combobox, FormField, Select, SelectOption } from "@soft-db/ui";
import { useSiteAlerts } from "../../api/alert-query";
import { useSites } from "../../api/site-query";
import { useSpaces } from "../../api/space-query";
import { useStations } from "../../api/station-query";
import AppTextTooltip from "../common/AppTextTooltip";
import { AuditTargetFilter } from "./audit-filter.type";

type TargetTypeSelection = {
	text: string;
	value: AuditTarget;
};

type TargetSelection = {
	text: string;

	/**
	 * ${targetId}-${parentId}
	 */
	value: string;

	keywords: string[];
};

export default defineComponent({
	name: "AuditTargetFilterConfiguration",
	props: {
		filter: {
			type: Object as PropType<AuditTargetFilter>,
		},
	},
	setup(props, { emit }) {
		const { t } = useI18n();

		const filter = useVModel(props, "filter", emit);

		const availableTargetTypes = computed<TargetTypeSelection[]>(() => [
			{
				value: "station",
				text: t("object.station"),
			},
			{
				value: "site",
				text: t("object.project"),
			},
			{
				value: "alert",
				text: t("object.alert"),
			},

			{
				value: "space",
				text: t("object.space"),
			},

			{
				value: "report",
				text: t("object.report"),
			},
		]);

		function updateTargetType(target: AuditTarget | null) {
			if (!filter.value || !target) return;

			filter.value.target = target;

			filter.value.targetId = "";
			filter.value.parentId = "";
		}

		const { stations } = useStations({ enabled: () => filter.value?.target === "station" });
		const { sites } = useSites({
			enabled: () => filter.value?.target === "site" || filter.value?.target === "alert",
		});

		const { alerts } = useSiteAlerts(() =>
			filter.value?.target === "alert" ? filter.value.parentId || "" : ""
		);

		const { spaces } = useSpaces({ enabled: () => filter.value?.target === "space" });

		const { scope } = useScopeStore();
		const { reports } = useScopeReports(scope);

		const stationTargets = computed<TargetSelection[]>(() => {
			return (
				stations.value?.map((s) => ({
					text: s.name,
					value: s.id,
					keywords: [s.serialNumber],
				})) || []
			);
		});

		const siteTargets = computed<TargetSelection[]>(() => {
			return (
				sites.value?.map((s) => ({
					text: s.name,
					value: s.id,
					keywords: [],
				})) || []
			);
		});

		const siteWithAlertsTargets = computed<TargetSelection[]>(() => {
			return sites.value
				.filter((s) => s.alerts > 0)
				.map((s) => ({
					text: s.name,
					value: s.id,
					keywords: [],
				}));
		});

		const spaceTargets = computed<TargetSelection[]>(() => {
			return (
				spaces.value?.map((s) => ({
					text: s.name,
					value: s.id,
					keywords: [],
				})) || []
			);
		});

		const alertTargets = computed<TargetSelection[]>(() => {
			return (
				alerts.value?.map((a) => ({
					text: a.name,
					value: a.id,
					keywords: [],
				})) || []
			);
		});

		const reportTargets = computed<TargetSelection[]>(() => {
			return (
				reports.value?.map((r) => ({
					text: r.title,
					value: r.id,
					keywords: [],
				})) || []
			);
		});

		const parentTargetType = computed<AuditTarget | null>(() => {
			if (filter.value?.target === "alert") {
				return "site";
			}
			return null;
		});

		const hasParentTarget = computed(() => {
			return parentTargetType.value != null;
		});

		const availableTargets = computed<TargetSelection[]>(() => {
			if (filter.value?.target == null) {
				return [];
			}

			switch (filter.value.target) {
				case "station":
					return stationTargets.value;
				case "site":
					return siteTargets.value;
				case "alert":
					return alertTargets.value;
				case "space":
					return spaceTargets.value;
				case "report":
					return reportTargets.value;
			}

			return [];
		});

		const availableParentTargets = computed<TargetSelection[]>(() => {
			if (parentTargetType.value === "site" && filter.value?.target === "alert") {
				return siteWithAlertsTargets.value;
			}

			return [];
		});

		function resolveTargetLabel(target?: AuditTarget) {
			if (target == null) {
				return t("common.object");
			}

			switch (target) {
				case "station":
					return t("object.station");
				case "site":
					return t("object.site");
				case "alert":
					return t("object.alert");
				case "space":
					return t("object.space");
				case "report":
					return t("object.report");
			}
		}

		const parentTargetLabel = computed(() => {
			if (parentTargetType.value != null) {
				return resolveTargetLabel(parentTargetType.value);
			}
			return "";
		});

		const targetLabel = computed(() => {
			return resolveTargetLabel(filter.value?.target);
		});

		const selectedTarget = computed(() => {
			return filter.value?.targetId || null;
		});

		const selectedParentTarget = computed(() => {
			return filter.value?.parentId || null;
		});

		function updateTarget(id: string | null) {
			if (filter.value == null || id == null) {
				return;
			}
			filter.value.targetId = id;
		}

		function updateParentTarget(id: string | null) {
			if (filter.value == null || id == null) {
				return;
			}

			// Second target is always the target, not the parent
			filter.value.parentId = id;
			filter.value.targetId = "";
		}

		function targetDisplay(target: TargetSelection) {
			return (
				<div class="flex items-center gap-2">
					<AppTextTooltip value={target.text} />
					{target.keywords.length > 0 && (
						<div class="text-muted">({target.keywords.join(" ")})</div>
					)}
				</div>
			);
		}

		return () => (
			<>
				<FormField label={t("audit.objectType")}>
					<Select
						class="w-full"
						modelValue={filter.value?.target}
						onUpdate:modelValue={updateTargetType}
					>
						<For each={availableTargetTypes.value}>
							{(target) => <SelectOption value={target.value}>{target.text}</SelectOption>}
						</For>
					</Select>
				</FormField>

				<Show when={filter.value?.target}>
					<Show when={hasParentTarget.value}>
						<FormField label={parentTargetLabel.value}>
							<Combobox
								modelValue={selectedParentTarget.value}
								options={availableParentTargets.value}
								optionLabel="text"
								optionValue="value"
								optionKeywords="keywords"
								sort
								virtual
								estimateHeight={32}
								onUpdate:modelValue={updateParentTarget}
							>
								{{
									option: ({ option }) => <AppTextTooltip value={option.text} />,
								}}
							</Combobox>
						</FormField>
					</Show>

					<FormField label={targetLabel.value}>
						<Combobox
							modelValue={selectedTarget.value}
							options={availableTargets.value}
							optionLabel="text"
							optionValue="value"
							optionKeywords="keywords"
							disabled={hasParentTarget.value && selectedParentTarget.value == null}
							sort
							virtual
							estimateHeight={32}
							onUpdate:modelValue={updateTarget}
						>
							{{
								selection: ({ option }) => targetDisplay(option),
								option: ({ option }) => targetDisplay(option),
							}}
						</Combobox>
					</FormField>
				</Show>
			</>
		);
	},
});
